import { useState } from 'react'
import { api, ApiError } from '../../api/client'
import { assessPassword, PASSWORD_MIN_LENGTH } from '../../lib/password'
import { Button, Card, Input } from '../ui'
import { PasswordStrengthMeter } from './PasswordStrengthMeter'
import { StepUpCancelled, StepUpLockHint, useStepUp } from './StepUp'

/** "Change password": current + new + confirm. Step-up gated server-side
 *  for factor-holding users — the 403 opens the StepUpModal via useStepUp
 *  and the change is retried with the token. The strength meter is
 *  guidance only; the server policy (min 12, denylist, no personal info)
 *  is the gate. */
export function ChangePasswordCard() {
  const withStepUp = useStepUp()

  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [changed, setChanged] = useState(false)

  const strength = assessPassword(next)
  const mismatch = confirm.length > 0 && next !== confirm
  const canSubmit = current.length > 0 && strength.meetsMinimum && next === confirm && !busy

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    setBusy(true)
    setError(null)
    setChanged(false)
    try {
      await withStepUp((token) =>
        api.changePassword({ current_password: current, new_password: next }, token ?? undefined),
      )
      setCurrent('')
      setNext('')
      setConfirm('')
      setChanged(true)
    } catch (err) {
      if (err instanceof StepUpCancelled) return
      if (err instanceof ApiError && err.status === 401) setError('Current password is incorrect.')
      else if (err instanceof ApiError) setError(err.detail)
      else setError('Could not change the password')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card
      title="Change password"
      action={<StepUpLockHint />}
    >
      <form className="flex flex-col gap-3" onSubmit={(e) => void submit(e)}>
        <Input
          label="Current password"
          type="password"
          autoComplete="current-password"
          required
          value={current}
          onChange={(e) => {
            setCurrent(e.target.value)
            setChanged(false)
          }}
        />
        <div className="flex flex-col gap-1.5">
          <Input
            label={`New password (at least ${PASSWORD_MIN_LENGTH} characters)`}
            type="password"
            autoComplete="new-password"
            required
            value={next}
            onChange={(e) => {
              setNext(e.target.value)
              setChanged(false)
            }}
          />
          <PasswordStrengthMeter password={next} />
        </div>
        <Input
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          required
          value={confirm}
          onChange={(e) => {
            setConfirm(e.target.value)
            setChanged(false)
          }}
        />
        {mismatch && <p className="text-xs text-ostrel-danger">The new passwords do not match.</p>}
        {error && <p className="text-xs text-ostrel-danger">{error}</p>}
        {changed && <p className="text-xs text-ostrel-ok">Password changed.</p>}
        <div className="flex justify-end">
          <Button type="submit" variant="navy" size="sm" disabled={!canSubmit}>
            {busy ? 'Changing…' : 'Change password'}
          </Button>
        </div>
      </form>
    </Card>
  )
}
